/*
 * Copyright 2023-2025 Steve Xiao (stevexmh@qq.com) and contributors.
 *
 * 本源代码文件是属于 AMLL TTML Tool 项目的一部分。
 * This source code file is a part of AMLL TTML Tool project.
 * 本项目的源代码的使用受到 GNU GENERAL PUBLIC LICENSE version 3 许可证の约束，具体可以参阅以下链接.
 * Use of this source code is governed by the GNU GPLv3 license that can be found through the following link.
 *
 * https://github.com/amll-dev/amll-ttml-tool/blob/main/LICENSE
 */

import { Card } from "@radix-ui/themes";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { audioEngine } from "$/modules/audio/audio-engine";
import {
	audioEngineStateAtom,
	currentDurationAtom,
	loadedAudioAtom,
	pcmDataReadyAtom,
} from "$/modules/audio/states";
import AnalyzerWorker from "$/modules/ffmpeg/worker/analyzer.worker.ts?worker";
import ffmpegWasmUrl from "$/modules/ffmpeg/worker/wasm/ffmpeg_wasm.wasm?url"; 
import { lyricLinesAtom, selectedLinesAtom } from "$/states/main";
import { useHoverGuide } from "../hooks";
import { AudioRegion } from "./AudioRegion";
import styles from "./AudioSlider.module.css";
import { HoverGuide } from "./HoverGuide";

export const AudioSlider = () => {
	const currentDuration = useAtomValue(currentDurationAtom);
	const engineState = useAtomValue(audioEngineStateAtom);
	const audioFile = useAtomValue(loadedAudioAtom);
	const setPcmDataReady = useSetAtom(pcmDataReadyAtom);
	const lyricLines = useAtomValue(lyricLinesAtom);
	const selectedLines = useAtomValue(selectedLinesAtom);

	const cardRef = useRef<HTMLDivElement>(null); 
	const wsContainerRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const cursorRef = useRef<HTMLDivElement>(null);

	const workerRef = useRef<Worker | null>(null);
	const offscreenTransferred = useRef(false);

	const [sliderWidthPx, setSliderWidthPx] = useState(0);

	const [sliderHeight, setSliderHeight] = useState(45); 
	const [zoomFactor, setZoomFactor] = useState(1);       

	const {
		hoverState,
		handleContainerMouseMove,
		handleContainerMouseLeave,
		isDraggingRef,
	} = useHoverGuide(sliderWidthPx * zoomFactor);

	const handleResizeMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		const startY = e.clientY;
		const startHeight = sliderHeight;

		const handleMouseMove = (moveEvent: MouseEvent) => {
			const deltaY = startY - moveEvent.clientY;
			setSliderHeight(Math.max(40, Math.min(400, startHeight + deltaY)));
		};

		const handleMouseUp = () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
	}, [sliderHeight]);

	const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
		if (e.altKey) {
			e.preventDefault(); 
			const factor = 1.15; 
			setZoomFactor((prev) => {
				const nextZoom = e.deltaY < 0 ? prev * factor : prev / factor;
				return Math.max(1, Math.min(100, nextZoom));
			});
		}
	}, []);

	useEffect(() => {
		workerRef.current = new AnalyzerWorker();

		workerRef.current.onmessage = (e) => {
			if (e.data.type === "ANALYZE_DONE") {
				setPcmDataReady(true);
			}
		};
		return () => {
			workerRef.current?.terminate();
		};
	}, [setPcmDataReady]);

	useEffect(() => {
		const container = cardRef.current; 
		if (!container) return;

		const observer = new ResizeObserver((entries) => {
			if (entries[0]) {
				setSliderWidthPx(entries[0].contentRect.width);
			}
		});
		observer.observe(container);
		setSliderWidthPx(container.clientWidth);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (
			!audioFile ||
			audioFile.size === 0 ||
			!workerRef.current ||
			!canvasRef.current ||
			!wsContainerRef.current
		) {
			return;
		}

		setPcmDataReady(false);

		let canvasPayload: OffscreenCanvas | undefined;
		let transfer: Transferable[] = [];

		if (!offscreenTransferred.current) {
			const offscreen = canvasRef.current.transferControlToOffscreen();
			canvasPayload = offscreen;
			transfer = [offscreen];
			offscreenTransferred.current = true;
		}
		const stylesObj = getComputedStyle(wsContainerRef.current);
		const waveColor =
			stylesObj.getPropertyValue("--accent-a4").trim() || "#00ffa21e";
		workerRef.current.postMessage(
			{
				type: "INIT",
				payload: {
					file: audioFile,
					ffmpegWasmUrl,
					canvas: canvasPayload,
					width: sliderWidthPx * zoomFactor, 
					height: wsContainerRef.current.clientHeight,
					dpr: window.devicePixelRatio || 1,
					color: waveColor,
				},
			},
			transfer,
		);
	}, [audioFile, setPcmDataReady, zoomFactor, sliderWidthPx]); 

	useEffect(() => {
		if (sliderWidthPx > 0 && workerRef.current && wsContainerRef.current) {
			const timeoutId = setTimeout(() => {
				if (!wsContainerRef.current || !workerRef.current) return;
				const stylesObj = getComputedStyle(wsContainerRef.current);
				const waveColor =
					stylesObj.getPropertyValue("--accent-a4").trim() || "#00ffa21e";
				workerRef.current.postMessage({
					type: "RESIZE",
					payload: {
						width: sliderWidthPx * zoomFactor, 
						height: wsContainerRef.current.clientHeight,
						dpr: window.devicePixelRatio || 1,
						color: waveColor,
					},
				});
			}, 150); 
			return () => clearTimeout(timeoutId);
		}
	}, [sliderWidthPx, sliderHeight, zoomFactor]); 

	useEffect(() => {
		if (engineState === "idle" && workerRef.current && sliderWidthPx > 0) {
			workerRef.current.postMessage({
				type: "RESIZE",
				payload: { width: 0, height: 0 },
			});
		}
	}, [engineState, sliderWidthPx]);

	useEffect(() => {
		let rafId: number;
		const renderCursor = () => {
			if (currentDuration > 0 && cursorRef.current && sliderWidthPx > 0) {
				const progress =
					audioEngine.musicCurrentTime / (currentDuration / 1000);
				const xPos = progress * (sliderWidthPx * zoomFactor); 
				cursorRef.current.style.transform = `translateX(${xPos}px)`;
			}
			rafId = requestAnimationFrame(renderCursor);
		};
		rafId = requestAnimationFrame(renderCursor);
		return () => cancelAnimationFrame(rafId);
	}, [currentDuration, sliderWidthPx, zoomFactor]);

	const selectedRegions = useMemo(() => {
		if (currentDuration <= 0 || sliderWidthPx <= 0) return [];

		const pixelsPerMs = (sliderWidthPx * zoomFactor) / currentDuration; 
		const regions: { id: string; left: number; width: number }[] = [];

		for (const line of lyricLines.lyricLines) {
			if (selectedLines.has(line.id)) {
				const left = line.startTime * pixelsPerMs;
				const width = (line.endTime - line.startTime) * pixelsPerMs;
				// 🛠️ 修正：壊れたアロー関数を完全排除し、本来の正しい変数割り当てに復元
				regions.push({ id: line.id, left, width }); 
			}
		}
		return regions;
	}, [lyricLines.lyricLines, selectedLines, currentDuration, sliderWidthPx, zoomFactor]);

	const handleTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		if (currentDuration <= 0 || sliderWidthPx <= 0) return;
		if (isDraggingRef.current) return;
		if (!cardRef.current) return;

		const wasPlayingBeforeScrub = audioEngine.musicPlaying;

		if (!wasPlayingBeforeScrub) {
			audioEngine.resumeMusic();
		}

		const updateScrubPosition = (clientX: number) => {
			if (!cardRef.current) return;
			const cardRect = cardRef.current.getBoundingClientRect();
			const clickXInCard = clientX - cardRect.left;
			const absoluteX = clickXInCard + cardRef.current.scrollLeft;

			const totalZoomedWidth = sliderWidthPx * zoomFactor;
			const progress = Math.max(0, Math.min(absoluteX / totalZoomedWidth, 1));
			
			audioEngine.seekMusic((progress * currentDuration) / 1000);
		};

		updateScrubPosition(e.clientX);

		const handleMouseMove = (moveEvent: MouseEvent) => {
			updateScrubPosition(moveEvent.clientX);
		};

		const handleMouseUp = () => {
			if (!wasPlayingBeforeScrub) {
				audioEngine.pauseMusic();
			}
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
	};

	return (
		<Card
			style={{
				alignSelf: "center",
				width: "100%",
				height: `${sliderHeight}px`, 
				padding: "0",
				position: "relative",
				overflow: "hidden", 
			}}
		>
			{/* 上の境界線の縦幅変更用つまみ */}
			<div 
				onMouseDown={handleResizeMouseDown}
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					height: "8px",
					cursor: "ns-resize",
					zIndex: 2000,
					backgroundColor: "transparent"
				}}
			/>

			<div
				ref={cardRef}
				onWheel={handleWheel}
				style={{
					width: "100%",
					height: "100%",
					overflowX: "auto",
					overflowY: "hidden",
				}}
			>
				<section
					className={styles.waveformContainer}
					aria-label="Audio Waveform"
					ref={wsContainerRef}
					style={{
						width: `${sliderWidthPx * zoomFactor}px`, 
						height: "100%",
						overflow: "hidden",
						position: "relative",
						cursor: "text",
					}}
					onMouseMove={handleContainerMouseMove}
					onMouseLeave={handleContainerMouseLeave}
					onMouseDown={handleTimelineMouseDown}
				>
					<canvas
						ref={canvasRef}
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							width: "100%",
							height: "100%",
							pointerEvents: "none",
					}}
					/>

					<HoverGuide hoverState={hoverState} />

					{selectedRegions.map((region) => (
						<div
							key={region.id}
							className={styles.selectedLyricRegion}
							style={{
								left: `${region.left}px`,
								width: `${region.width}px`,
							}}
						/>
					))}

					{currentDuration > 0 && (
						<div
							ref={cursorRef}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "1px",
								height: "100%",
								backgroundColor: "var(--accent-a11)",
								pointerEvents: "none",
								zIndex: 20,
								willChange: "transform",
							}}
						/>
					)}

					<AudioRegion
						sliderWidthPx={sliderWidthPx * zoomFactor} 
						containerRef={wsContainerRef}
						isDraggingRef={isDraggingRef}
					/>
				</section>
			</div>
		</Card>
	);
};